import { Alert, Field, Select, cx } from "./ui";
import { CARRIED, NOT_CARRIED, PARCEL_TYPES, categoryById, categoryMessage } from "@/lib/parcelCatalog";
import type { Parcel } from "@/lib/types";

// What we carry opens the list; what we do not is a separate section at the
// bottom. It stays on the list rather than being hidden: somebody shipping a
// case of wine needs to find out here, not at the door.
const CATEGORY_LEAD = [{ value: "", label: "Choose the closest match" }];
const CATEGORY_GROUPS = [
  { label: "What we carry", options: CARRIED.map((c) => ({ value: c.id, label: c.label })) },
  { label: "We cannot carry these", options: NOT_CARRIED.map((c) => ({ value: c.id, label: c.label })) },
];

/**
 * One parcel's type + contents + dimensions, shared between the merchant
 * booking flow and the guest booking flow so the courier's real size/weight
 * tiers and prohibited-items list only live in one place.
 */
export function ParcelFields({
  parcel,
  categoryId,
  onChangeParcel,
  onChangeCategoryId,
  errors = {},
  prefix,
}: {
  parcel: Parcel;
  categoryId: string | null;
  onChangeParcel: (next: Partial<Parcel>) => void;
  onChangeCategoryId: (next: string | null) => void;
  errors?: Record<string, string>;
  prefix: string;
}) {
  const activeType = PARCEL_TYPES.find(
    (type) =>
      type.preset.length_mm === parcel.length_mm &&
      type.preset.width_mm === parcel.width_mm &&
      type.preset.height_mm === parcel.height_mm &&
      type.preset.weight_g === parcel.weight_g,
  );
  const category = categoryById(categoryId);
  const message = categoryMessage(category);

  return (
    <div className="space-y-4">
      <div>
        <p className="ws-label">Parcel type</p>
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
          {PARCEL_TYPES.map((type) => (
            <button
              key={type.id}
              type="button"
              onClick={() => onChangeParcel({ ...type.preset })}
              className={cx(
                "rounded-xl border p-3 text-left text-sm transition",
                activeType?.id === type.id
                  ? "border-brand bg-brand-soft text-brand"
                  : "border-ink-200 hover:border-ink-300",
              )}
            >
              <p className="font-medium">{type.label}</p>
              <p className="mt-0.5 text-xs text-ink-400">{type.hint}</p>
            </button>
          ))}
        </div>
      </div>

      <Select
        label="What's inside?"
        name={`${prefix}-category`}
        required
        value={categoryId ?? ""}
        onChange={(e) => onChangeCategoryId(e.target.value || null)}
        options={CATEGORY_LEAD}
        groups={CATEGORY_GROUPS}
      />
      {message &&
        (message.tone === "bad" ? (
          <Alert tone="bad">{message.text}</Alert>
        ) : (
          // A packing tip is not a problem, and dressing one as a red banner
          // teaches people to ignore the red banners that are.
          <p className="text-sm text-ink-500">{message.text}</p>
        ))}
      {categoryId === "other" && (
        <Field
          label="Describe what's inside"
          required
          value={parcel.description ?? ""}
          onChange={(e) => onChangeParcel({ description: e.target.value })}
          placeholder="Cake boxes, documents, spare parts"
        />
      )}

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Field
          label="Length (mm)"
          type="number"
          required
          min={1}
          value={parcel.length_mm}
          onChange={(e) => onChangeParcel({ length_mm: Number(e.target.value) })}
          error={errors[`${prefix}.length_mm`]}
        />
        <Field
          label="Width (mm)"
          type="number"
          required
          min={1}
          value={parcel.width_mm}
          onChange={(e) => onChangeParcel({ width_mm: Number(e.target.value) })}
          error={errors[`${prefix}.width_mm`]}
        />
        <Field
          label="Height (mm)"
          type="number"
          required
          min={1}
          value={parcel.height_mm}
          onChange={(e) => onChangeParcel({ height_mm: Number(e.target.value) })}
          error={errors[`${prefix}.height_mm`]}
        />
        <Field
          label="Weight (g)"
          type="number"
          required
          min={1}
          value={parcel.weight_g}
          onChange={(e) => onChangeParcel({ weight_g: Number(e.target.value) })}
          error={errors[`${prefix}.weight_g`]}
          hint="Carriers charge on the greater of actual and volumetric weight."
        />
      </div>
    </div>
  );
}

export function isProhibited(categoryId: string | null): boolean {
  return categoryById(categoryId)?.level === "prohibited";
}

export function describedParcel(parcel: Parcel, categoryId: string | null): Parcel {
  const category = categoryById(categoryId);
  if (!category || category.id === "other") return parcel;
  return { ...parcel, description: category.label };
}
